<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type {
    CustomerCase as Case,
    CustomerCase,
    PatchedCustomerCaseRequest
  } from '@/types/generated/types.gen'
  import { RelationshipTypeEnum, EntityTypeEnum } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useUserStore } from '@/store/userStore'

  const props = defineProps<{
    debounceDelay: number
    throttleInterval: number
    selectedEntitiesRelationships?: Array<string>
    mappedEntityTypes?: Array<string>
  }>()

  const emit = defineEmits(['update:payload'])

  const { t } = useTranslation({
    en: {
      clerk: 'Clerk',
      assistant: 'Assistant',
      actaportSection: 'Actaport Information',
      clerkRequired: 'Clerk is required',
      assistantRequired: 'Assistant is required',
      title: 'Title',
      titleRequired: 'Title is required'
    },
    de: {
      clerk: 'Sachbearbeiter',
      assistant: 'Assistent',
      actaportSection: 'Actaport Information',
      clerkRequired: 'Sachbearbeiter ist erforderlich',
      assistantRequired: 'Assistent ist erforderlich',
      title: 'Aktenbezeichnung',
      titleRequired: 'Aktenbezeichnung ist erforderlich'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const userStore = useUserStore()

  const isClientEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  }

  const isOpponentEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  }

  const getClientEntity = computed(() => {
    if (!caseRecord.value?.data.entities) return null
    return caseRecord.value.data.entities.find((entity) => isClientEntity(entity))
  })

  const getOpponentEntity = computed(() => {
    if (!caseRecord.value?.data.entities) return null
    return caseRecord.value.data.entities.find((entity) => isOpponentEntity(entity))
  })

  const defaultTitle = computed(() => {
    if (!caseRecord.value) return ''

    const clientEntity = getClientEntity.value
    const opponentEntity = getOpponentEntity.value
    const mappedTypes = props.mappedEntityTypes || ['client'] // Default to client

    if (!clientEntity) return ''

    if (caseRecord.value.data.law.field === 'Strafrecht') {
      return `${clientEntity.entity?.first_name || ''} ${clientEntity.entity?.last_name || ''}`.trim()
    }

    // Format client name based on entity type
    const clientName =
      clientEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
        ? `${clientEntity.entity?.company_name || ''} ${clientEntity.entity?.company_legal_form || 'GmbH'}`
        : clientEntity.entity?.last_name || ''

    // Default title with just the client
    let title = `${clientName} ./.`

    // Check if opponent is included in mapped entity types and exists
    const includeOpponent = opponentEntity && mappedTypes.includes('opponent')

    if (includeOpponent) {
      // Format opponent name based on entity type
      const opponentName =
        opponentEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
          ? `${opponentEntity.entity?.company_name || ''} ${opponentEntity.entity?.company_legal_form || 'GmbH'}`
          : opponentEntity.entity?.last_name || ''

      title = `${clientName} ./. ${opponentName}`
    }

    return title
  })

  const actaportClerkOptions = computed(() =>
    (userStore.user?.org?.actaport_clerk_options || []).map((option: { id: any; name: any }) => ({
      value: option.id,
      title: option.name
    }))
  )

  const actaportAssistantOptions = computed(() =>
    (userStore.user?.org?.actaport_assistant_options || []).map(
      (option: { id: any; name: any }) => ({
        value: option.id,
        title: option.name
      })
    )
  )

  const schema = {
    clerk: yup.string().required(t('clerkRequired')),
    assistant: yup.string().required(t('assistantRequired')),
    title: yup.string().required(t('titleRequired'))
  }

  interface FormData {
    clerk: string
    assistant: string
    title: string
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation<FormData>(
    {
      clerk: actaportClerkOptions.value[0]?.value || '',
      assistant: actaportAssistantOptions.value[0]?.value || '',
      title: defaultTitle.value
    },
    schema
  )

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  watch(
    defaultTitle,
    (newValue) => {
      formData.title = newValue
    },
    { deep: true }
  )

  // Watch for changes to mappedEntityTypes directly
  watch(
    () => props.mappedEntityTypes,
    () => {
      // Force title update
      formData.title = defaultTitle.value
      // Notify parent of the change
      handleSave(formData)
    },
    { deep: true }
  )

  // Watch for changes to the entities
  watch(
    () => caseRecord.value?.data.entities,
    () => {
      // Force title update when entity data changes
      formData.title = defaultTitle.value
      // Notify parent of the change
      handleSave(formData)
    },
    { deep: true }
  )

  watch(
    () => formData.title,
    (newValue) => {}
  )

  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )

  onMounted(() => {
    handleSave({
      title: defaultTitle.value,
      clerk: actaportClerkOptions.value[0]?.value || '',
      assistant: actaportAssistantOptions.value[0]?.value || ''
    })
  })

  defineExpose({
    validate,
    showErrors
  })
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold">{{ t('actaportSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-4">
        <div>
          <InputField
            v-model="formData.title"
            :label="t('title')"
            name="title"
            :error="formErrors?.value?.title ?? null"
            :rules="yup.string().required(t('titleRequired'))"
            @update:model-value="(value: any) => handleFieldChange('title', value)"
            @focus="() => handleFieldTouch('title')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('clerk') }}
          </label>
          <SelectField
            v-model="formData.clerk"
            :options="actaportClerkOptions"
            name="clerk"
            :placeholder="t('clerk')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.clerk ?? null"
            @update:model-value="(value: any) => handleFieldChange('clerk', value)"
            @focus="() => handleFieldTouch('clerk')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('assistant') }}
          </label>
          <SelectField
            v-model="formData.assistant"
            :options="actaportAssistantOptions"
            name="assistant"
            :placeholder="t('assistant')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.assistant ?? null"
            @update:model-value="(value: any) => handleFieldChange('assistant', value)"
            @focus="() => handleFieldTouch('assistant')"
          />
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>
